import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getPage, listVersions, PageVersionRow } from "../lib/api";
import { computeDiff } from "../lib/diff";

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  pending_review: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  approved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  published: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

// ── Component ───────────────────────────────────────────────────────────────

export default function VersionHistoryPage({ pageSlug: pageSlugProp }: { pageSlug?: string }) {
  const { username, spaceSlug, pageSlug: pageSlugParam } = useParams<{
    username: string; spaceSlug: string; pageSlug: string;
  }>();
  const pageSlug = pageSlugProp ?? pageSlugParam ?? "";

  const [versions, setVersions] = useState<PageVersionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!username || !spaceSlug || !pageSlug) return;
    getPage(username, spaceSlug, pageSlug)
      .then((p) => listVersions(p.id))
      .then(setVersions)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [username, spaceSlug, pageSlug]);

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Version History</h1>
          <Link
            to={`/${username}/${spaceSlug}/${pageSlug}/edit`}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Edit page →
          </Link>
        </div>

        {loading && (
          <div className="space-y-3 animate-pulse">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-lg" />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        {!loading && !error && versions.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No versions yet.</p>
        )}

        {!loading && !error && versions.length > 0 && (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-800" />

            <div className="space-y-4">
              {versions.map((v, i) => {
                const prev = versions[i + 1]; // older version (list is newest-first)
                const diffLines = expandedId === v.id && v.content
                  ? computeDiff(prev?.content ?? "", v.content)
                  : null;
                const added = diffLines?.filter((l) => l.type === "added").length ?? 0;
                const removed = diffLines?.filter((l) => l.type === "removed").length ?? 0;

                return (
                  <div key={v.id} className="flex gap-4">
                    {/* Dot */}
                    <div className={`shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs z-10 ${
                      v.status === "published"
                        ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30"
                        : v.status === "rejected"
                        ? "border-red-400 bg-red-50 dark:bg-red-900/30"
                        : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                    }`}>
                      {i + 1}
                    </div>

                    {/* Card */}
                    <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg mb-1 overflow-hidden">
                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[v.status] ?? "bg-gray-100 text-gray-600"}`}>
                              {v.status.replace("_", " ")}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              by {v.author_type}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-600">
                              {new Date(v.created_at).toLocaleString()}
                            </span>
                          </div>
                          {v.content && (
                            <button
                              onClick={() => setExpandedId(expandedId === v.id ? null : v.id)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
                            >
                              {expandedId === v.id ? "Hide diff" : i === versions.length - 1 ? "View content" : "View diff"}
                            </button>
                          )}
                        </div>
                        {v.commit_message && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 italic">
                            "{v.commit_message}"
                          </p>
                        )}
                        {v.reject_reason && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Rejected: {v.reject_reason}
                          </p>
                        )}
                      </div>

                      {/* Diff panel */}
                      {expandedId === v.id && diffLines && (
                        <div className="border-t border-gray-100 dark:border-gray-800">
                          {/* Stat bar */}
                          <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 text-xs">
                            {i === versions.length - 1 ? (
                              <span className="text-gray-500 dark:text-gray-400">Initial version</span>
                            ) : (
                              <>
                                <span className="text-emerald-600 dark:text-emerald-400 font-mono">+{added}</span>
                                <span className="text-red-500 dark:text-red-400 font-mono">−{removed}</span>
                                <span className="text-gray-400 dark:text-gray-500">{diffLines.filter(l => l.type === "unchanged").length} unchanged</span>
                              </>
                            )}
                          </div>
                          {/* Diff lines */}
                          <div className="overflow-x-auto max-h-96 overflow-y-auto">
                            <pre className="text-xs font-mono leading-5">
                              {diffLines.map((line, li) => (
                                <div
                                  key={li}
                                  className={
                                    line.type === "added"
                                      ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 px-4"
                                      : line.type === "removed"
                                      ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 line-through opacity-70"
                                      : "text-gray-600 dark:text-gray-400 px-4"
                                  }
                                >
                                  <span className="select-none mr-2 opacity-40">
                                    {line.type === "added" ? "+" : line.type === "removed" ? "−" : " "}
                                  </span>
                                  {line.text || " "}
                                </div>
                              ))}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
